import React, { useEffect, createContext, useState, useCallback } from 'react';
import { get, post, put, patch, remove } from '../service/ApiService';
import { getCommonUtils } from '../service/CorporateWellDhanService';
import { getUserProfileDetails } from '../service/AuthWellDhanService';
import { profileCache } from '../utils/profileCache';
import { clearAllListCaches } from '../utils/listCache/listCacheStore';
import { clear as clearRoomMessagesCache } from '../hooks/comms/roomMessagesCache';
import { clearMediaCache } from '../hooks/comms/mediaUrlCache';
import { clearLinksMetaCache } from '../features/links/core/hooks/linksMetaCache';
import { resetThumbnailCache as clearQrThumbnailCache } from '../features/links/workflow/print/useQrThumbnails';

export const ApiContext = createContext();

export const ApiProvider = ({ children }) => {
  const [mailId, setMailId] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('access_token'));

  // User-specific client-id
  const [userId, setUserId] = useState(() => {
    try {
      const cached = localStorage.getItem('userId');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });

  // Employee-specific (from Corporate)
  const [empId, setEmpId] = useState(null);
  const [empDept, setEmpDept] = useState('');
  const [empRole, setEmpRole] = useState('');
  const [empName, setEmpName] = useState('');
  const [empMail, setEmpMail] = useState('');
  const [userType, setUserType] = useState('');
  // Full employee data from get-current-employee API (includes roles).
  // Initialize from localStorage so PrivateRoute sees it on first paint (no flash of null / forced wait).
  const [employeeData, setEmployeeData] = useState(() => {
    try {
      const cached = localStorage.getItem("current_employee_data");
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  
  // Departments with roles from getCommonUtils
  const [departmentsWithRoles, setDepartmentsWithRoles] = useState(() => {
    try {
      const cached = localStorage.getItem('departments_with_roles');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });

  // Rehydrate employee info from localStorage on first load
  useEffect(() => {
    try {
      const cached = localStorage.getItem('employee_info');
      if (cached) {
        const info = JSON.parse(cached);
        setEmpId(info.empId ?? null);
        setEmpDept(info.empDept ?? '');
        setEmpRole(info.empRole ?? '');
        setEmpName(info.empName ?? '');
        setEmpMail(info.empMail ?? '');
        setUserType(info.userType ?? '');
      }
    } catch (e) {
      // ignore parse errors
    }
  }, []);

  // Persist userId
  useEffect(() => {
    if (userId) localStorage.setItem('userId', JSON.stringify(userId));
    else localStorage.removeItem('userId');
  }, [userId]);

  const updateMailId = (mail) => setMailId(mail);

  const login = () => setIsAuthenticated(true);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) setIsAuthenticated(true);
  }, []);

  // Single update function for all EMPLOYEE info + persist
  const setEmployeeInfo = useCallback(
    ({
      empId = null,
      empDept = '',
      empRole = '',
      empName = '',
      empMail = '',
      userType = '',
    }) => {
      setEmpId(empId);
      setEmpDept(empDept);
      setEmpRole(empRole);
      setEmpName(empName);
      setEmpMail(empMail);
      setUserType(userType);

      // Persist to localStorage so refresh keeps it
      try {
        localStorage.setItem(
          'employee_info',
          JSON.stringify({ empId, empDept, empRole, empName, empMail, userType })
        );
      } catch {}
    },
    []
  );

  // Function to set employee data from get-current-employee API
  const setCurrentEmployeeData = useCallback((data) => {
    setEmployeeData(data);
    // Store in localStorage for persistence
    try {
      if (data) {
        localStorage.setItem('current_employee_data', JSON.stringify(data));
      } else {
        localStorage.removeItem('current_employee_data');
      }
    } catch (e) {
      console.error('Error storing employee data:', e);
    }
  }, []);

  // Function to fetch and set departments with roles
  const fetchDepartmentsWithRoles = useCallback(async () => {
    try {
      const result = await getCommonUtils();
      if (result.success && result.data) {
        const departments = result.data.departments_with_roles || [];
        setDepartmentsWithRoles(departments);
        // Store in localStorage for persistence
        try {
          localStorage.setItem('departments_with_roles', JSON.stringify(departments));
        } catch (e) {
          console.error('Error storing departments_with_roles:', e);
        }
        return departments;
      }
    } catch (error) {
      console.error('Error fetching departments with roles:', error);
    }
    return null;
  }, []);

  // Rehydrate departments with roles from localStorage on first load
  useEffect(() => {
    try {
      const cached = localStorage.getItem('departments_with_roles');
      if (cached) {
        setDepartmentsWithRoles(JSON.parse(cached));
      }
    } catch (e) {
      // ignore parse errors
    }
  }, []);

  // User profile data state
  const [userProfileData, setUserProfileData] = useState(() => {
    // Try to load from cache on init
    const cached = profileCache.get();
    return cached || null;
  });
  const [userProfileLoading, setUserProfileLoading] = useState(false);

  // Function to fetch user profile (checks cache first)
  const fetchUserProfile = useCallback(async (sessionUtils) => {
    // Check cache first
    const cached = profileCache.get();
    if (cached) {
      setUserProfileData(cached);
      return { success: true, data: cached, fromCache: true };
    }

    // If no valid cache, fetch from API
    setUserProfileLoading(true);
    try {
      const eventTypeId = sessionUtils?.event_types?.[0]?.event_types?.find(
        (x) => x.description === "get_user_profile_details"
      )?.id;

      if (!eventTypeId) {
        console.warn("Event type id for get_user_profile_details not found");
        setUserProfileLoading(false);
        return { success: false, message: "Event type id not found" };
      }

      const profileRes = await getUserProfileDetails(eventTypeId);

      if (profileRes?.success && profileRes.data) {
        const payload = profileRes.data.data ?? profileRes.data;
        
        // Store in cache and state
        profileCache.set(payload);
        setUserProfileData(payload);
        setUserProfileLoading(false);
        return { success: true, data: payload, fromCache: false };
      } else {
        setUserProfileLoading(false);
        return { success: false, message: profileRes?.message || "Failed to fetch profile" };
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      setUserProfileLoading(false);
      return { success: false, message: "Error fetching user profile" };
    }
  }, []);

  // Function to force refresh user profile (bypasses cache)
  const refreshUserProfile = useCallback(async (sessionUtils) => {
    setUserProfileLoading(true);
    try {
      const eventTypeId = sessionUtils?.event_types?.[0]?.event_types?.find(
        (x) => x.description === "get_user_profile_details"
      )?.id;

      if (!eventTypeId) {
        console.warn("Event type id for get_user_profile_details not found");
        setUserProfileLoading(false);
        return { success: false, message: "Event type id not found" };
      }

      const profileRes = await getUserProfileDetails(eventTypeId);

      if (profileRes?.success && profileRes.data) {
        const payload = profileRes.data.data ?? profileRes.data;
        
        // Update cache and state
        profileCache.set(payload);
        setUserProfileData(payload);
        setUserProfileLoading(false);
        return { success: true, data: payload };
      } else {
        setUserProfileLoading(false);
        return { success: false, message: profileRes?.message || "Failed to refresh profile" };
      }
    } catch (error) {
      console.error("Error refreshing user profile:", error);
      setUserProfileLoading(false);
      return { success: false, message: "Error refreshing user profile" };
    }
  }, []);

  // Function to log out
  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('employee_info'); // clear cached employee details
    localStorage.removeItem('current_employee_data'); // clear employee data
    localStorage.removeItem('userId');
    profileCache.clear(); // clear profile cache
    clearAllListCaches(); // clear cached lists (templates, users, recipes) — no cross-account leakage
    // Message bodies are PII and this cache is a module singleton, so without
    // this the previous account's threads survive into the next login.
    clearRoomMessagesCache();
    // Signed media URLs are scoped credentials — drop them with the session.
    clearMediaCache();
    // QR & Links: the cached S1 `caller` is per user (U79).
    clearLinksMetaCache();
    // QR thumbnails are per-user object URLs: revoke them all (U63).
    clearQrThumbnailCache();
    // Note: We keep departments_with_roles in localStorage as it's not user-specific
    setIsAuthenticated(false);
    setMailId('');
    setUserId(null);
    // reset employee state
    setEmpId(null);
    setEmpDept('');
    setEmpRole('');
    setEmpName('');
    setEmpMail('');
    setUserType('');
    setEmployeeData(null);
    setUserProfileData(null); // clear profile data
  };

  return (
    <ApiContext.Provider
      value={{
        get, post, put, remove, patch,
        userId, setUserId,
        // Employee
        empId, empDept, empRole, empName, empMail, userType,
        setEmpId, setEmpDept, setEmpRole, setEmpName, setEmpMail, setUserType,
        setEmployeeInfo,
        // Employee data from get-current-employee API
        employeeData, setCurrentEmployeeData,
        // Departments with roles
        departmentsWithRoles, fetchDepartmentsWithRoles,
        // User profile data
        userProfileData, userProfileLoading, fetchUserProfile, refreshUserProfile,
        mailId, updateMailId,
        isAuthenticated,
        login, logout,
      }}
    >
      {children}
    </ApiContext.Provider>
  );
};
